import { authenticatedFetch } from "./authenticatedFetch";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL;
export const DOCUMENT_FILLER_TICKET_PATH =
  import.meta.env.VITE_AI_DOCUMENT_FILLER_TICKET_PATH ?? "/document-filler/ws-ticket";
export const DOCUMENT_FILLER_JOBS_PATH =
  import.meta.env.VITE_AI_DOCUMENT_FILLER_JOBS_PATH ?? "/document-filler/jobs";

function formatHttpError(payload) {
  if (payload == null) return "Não foi possível completar o pedido.";
  if (typeof payload === "string") return payload;
  if (typeof payload === "object") {
    return (
      payload.error ||
      payload.message ||
      payload.detail ||
      "Não foi possível completar o pedido."
    );
  }
  return "Não foi possível completar o pedido.";
}

async function readJson(response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Requests a short-lived ticket used to authenticate the document-filler socket.
 *
 * @returns {Promise<{ ok: true, ticket: string, expiresIn?: number } | { ok: false, message: string }>}
 */
export async function requestDocumentFillerTicket() {
  try {
    const response = await authenticatedFetch(
      `${API_BASE_URL}${DOCUMENT_FILLER_TICKET_PATH}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      }
    );
    const data = await readJson(response);

    if (!response.ok) {
      return { ok: false, message: formatHttpError(data) };
    }

    if (!data || typeof data !== "object" || typeof data.ticket !== "string") {
      return { ok: false, message: "Resposta inválida do servidor." };
    }

    const result = { ok: true, ticket: data.ticket };
    if (data.expires_in !== undefined) result.expiresIn = data.expires_in;
    return result;
  } catch (error) {
    console.error("requestDocumentFillerTicket:", error);
    return {
      ok: false,
      message: "Erro de rede. Verifique sua conexão e tente novamente.",
    };
  }
}

/**
 * Starts an asynchronous document-filler job.
 *
 * @param {{
 *   userData: string,
 *   formData: object,
 *   templateKey: string,
 *   fieldLabels?: Record<string, string>,
 *   formStructure?: Record<string, object>,
 * }} params
 * @returns {Promise<
 *   | { ok: true, jobId: string }
 *   | { ok: false, insufficientCoins?: boolean, message: string, code?: string }
 * >}
 */
export async function createDocumentFillerJob({
  userData,
  formData,
  templateKey,
  fieldLabels,
  formStructure,
}) {
  try {
    const body = {
      user_data: userData,
      form_data: formData,
      template_key: templateKey,
    };
    if (
      fieldLabels &&
      typeof fieldLabels === "object" &&
      !Array.isArray(fieldLabels) &&
      Object.keys(fieldLabels).length > 0
    ) {
      body.field_labels = fieldLabels;
    }
    if (
      formStructure &&
      typeof formStructure === "object" &&
      !Array.isArray(formStructure) &&
      Object.keys(formStructure).length > 0
    ) {
      body.form_structure = formStructure;
    }

    const response = await authenticatedFetch(`${API_BASE_URL}${DOCUMENT_FILLER_JOBS_PATH}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
    const data = await readJson(response);

    if (data?.valid === false && data.code === "insufficient_coins") {
      return {
        ok: false,
        insufficientCoins: true,
        message: formatHttpError(data),
        code: data.code,
      };
    }

    if (response.status === 202 && data && typeof data.job_id === "string") {
      return { ok: true, jobId: data.job_id };
    }

    if (!response.ok) {
      return {
        ok: false,
        message: formatHttpError(data),
        ...(typeof data?.code === "string" ? { code: data.code } : {}),
      };
    }

    return { ok: false, message: "Resposta inválida do servidor." };
  } catch (error) {
    console.error("createDocumentFillerJob:", error);
    return {
      ok: false,
      message: "Erro de rede. Verifique sua conexão e tente novamente.",
    };
  }
}
